import type {
  MeasurementAnswerInput,
  MeasurementDetails,
} from '@/src/dal/measurement-dal'
import type {
  MeasurementDefinitionDetail,
  QuestionDefinition,
} from '@/src/dal/session-dal'
import type { AudioSource } from 'expo-audio'
import { buildMeasurementRecordingAudioSource } from '@/src/dal/measurement-dal'

export interface VoiceAssessmentQuestionAnswer {
  readonly question: QuestionDefinition
  readonly choiceIndices: readonly number[]
  readonly selectedOptionLabels: readonly string[]
}

export interface VoiceAssessmentCombinedMeasurement {
  readonly definition: MeasurementDefinitionDetail
  readonly measurementId: number | null
  readonly recordingSource: AudioSource | null
  readonly hasSavedMeasurement: boolean
  readonly answers: readonly VoiceAssessmentQuestionAnswer[]
}

interface BuildVoiceAssessmentMeasurementAnswersParams {
  readonly measurementDefinitions: readonly MeasurementDefinitionDetail[]
  readonly measurementDetails: readonly MeasurementDetails[]
}

function groupMeasurementsByDefinitionId(
  measurements: readonly MeasurementDetails[],
) {
  const measurementsByDefinitionId = new Map<number, MeasurementDetails[]>()

  for (const measurement of measurements) {
    const definitionMeasurements =
      measurementsByDefinitionId.get(measurement.definitionId) ?? []
    definitionMeasurements.push(measurement)
    measurementsByDefinitionId.set(
      measurement.definitionId,
      definitionMeasurements,
    )
  }

  return measurementsByDefinitionId
}

function buildQuestionAnswers({
  questions,
  savedAnswers,
}: {
  readonly questions: readonly QuestionDefinition[]
  readonly savedAnswers: readonly MeasurementAnswerInput[]
}): VoiceAssessmentQuestionAnswer[] {
  const savedAnswersByQuestionId = new Map(
    savedAnswers.map((answer) => [answer.questionId, answer]),
  )

  return questions.map((question) => {
    const choiceIndices =
      savedAnswersByQuestionId.get(question.id)?.choiceIndices ?? []

    return {
      question,
      choiceIndices,
      selectedOptionLabels: choiceIndices.flatMap((choiceIndex) => {
        const option = question.options[choiceIndex]
        return option == null ? [] : [option]
      }),
    }
  })
}

function measurementDefinitionHasAudioTrack(
  definition: Pick<MeasurementDefinitionDetail, 'audioTrack'>,
) {
  return (definition.audioTrack?.trim().length ?? 0) > 0
}

export function buildVoiceAssessmentMeasurementAnswers({
  measurementDefinitions,
  measurementDetails,
}: BuildVoiceAssessmentMeasurementAnswersParams): VoiceAssessmentCombinedMeasurement[] {
  const measurementsByDefinitionId =
    groupMeasurementsByDefinitionId(measurementDetails)
  const detailsByMeasurementId = new Map(
    measurementDetails.map((measurementDetail) => [
      measurementDetail.id,
      measurementDetail,
    ]),
  )

  return measurementDefinitions.map((definition) => {
    const definitionMeasurements =
      measurementsByDefinitionId.get(definition.id) ?? []
    const measurement = definitionMeasurements.shift() ?? null
    const measurementDetail =
      measurement == null ? null : detailsByMeasurementId.get(measurement.id)
    const savedAnswers = measurementDetail?.answers ?? []

    return {
      definition,
      measurementId: measurement?.id ?? null,
      recordingSource:
        measurement != null && measurementDefinitionHasAudioTrack(definition)
          ? buildMeasurementRecordingAudioSource({
              measurementId: measurement.id,
            })
          : null,
      hasSavedMeasurement: measurementDetail != null,
      answers: buildQuestionAnswers({
        questions: definition.questions,
        savedAnswers,
      }),
    }
  })
}
